import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, getOrganizationSchema } from '../../utils/seo';
import SiteFooter from '../../components/feature/SiteFooter';
import SiteNav from '../../components/feature/SiteNav';

export default function Datenschutz() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const [showBackToTop, setShowBackToTop] = useState(false);

  // SEO Configuration
  useSEO({
    title: 'Datenschutzerklärung – Seitenschmied',
    description: 'Datenschutzerklärung von Seitenschmied. Informationen zum Datenschutz und zur Verarbeitung personenbezogener Daten gemäß DSGVO.',
    canonical: `${siteUrl}/datenschutz`,
    noindex: true,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        getOrganizationSchema(),
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/datenschutz#webpage`,
          url: `${siteUrl}/datenschutz`,
          name: 'Datenschutzerklärung – Seitenschmied',
          description: 'Datenschutzerklärung von Seitenschmied gemäß DSGVO.',
          inLanguage: 'de-DE',
          publisher: { '@id': `${siteUrl}/#organization` },
          noIndex: true,
        },
      ],
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      <SiteNav navId="datenschutz-nav" />

      {/* Hero */}
      <div className="relative pt-24 sm:pt-32 pb-12 sm:pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-emerald-50 to-cyan-50"></div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 rounded-full bg-teal-100 text-teal-700 font-bold text-sm mb-4 sm:mb-6">
            <i className="ri-shield-keyhole-line text-base sm:text-lg"></i>
            <span>Rechtliches</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Datenschutzerklärung
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            Informationen zum Datenschutz gemäß DSGVO
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
        <div className="bg-white rounded-2xl border-2 border-teal-200 shadow-lg p-6 sm:p-8 space-y-6 sm:space-y-8">
          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-eye-line text-sm text-white"></i>
              </span>
              1. Datenschutz auf einen Blick
            </h2>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Allgemeine Hinweise
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten
              passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie
              persönlich identifiziert werden können.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-database-2-line text-sm text-white"></i>
              </span>
              2. Datenerfassung auf dieser Website
            </h2>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Wer ist verantwortlich für die Datenerfassung auf dieser Website?
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können
              Sie dem Impressum dieser Website entnehmen.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Wie erfassen wir Ihre Daten?
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z.B. um
              Daten handeln, die Sie in ein Kontaktformular eingeben.
            </p>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Andere Daten werden automatisch oder nach Ihrer Einwilligung beim Besuch der Website durch unsere
              IT-Systeme erfasst. Das sind vor allem technische Daten (z.B. Internetbrowser, Betriebssystem oder
              Uhrzeit des Seitenaufrufs).
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Wofür nutzen wir Ihre Daten?
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Ein Teil der Daten wird erhoben, um eine fehlerfreie Bereitstellung der Website zu gewährleisten. Andere
              Daten können zur Analyse Ihres Nutzerverhaltens verwendet werden.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Welche Rechte haben Sie bezüglich Ihrer Daten?
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Sie haben jederzeit das Recht, unentgeltlich Auskunft über Herkunft, Empfänger und Zweck Ihrer
              gespeicherten personenbezogenen Daten zu erhalten. Sie haben außerdem ein Recht, die Berichtigung oder
              Löschung dieser Daten zu verlangen.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-server-line text-sm text-white"></i>
              </span>
              3. Hosting
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Wir hosten die Inhalte unserer Website bei folgendem Anbieter:
            </p>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Externes Hosting
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Diese Website wird extern gehostet. Die personenbezogenen Daten, die auf dieser Website erfasst werden,
              werden auf den Servern des Hosters / der Hoster gespeichert. Hierbei kann es sich v. a. um IP-Adressen,
              Kontaktanfragen, Meta- und Kommunikationsdaten, Vertragsdaten, Kontaktdaten, Namen, Websitezugriffe und
              sonstige Daten, die über eine Website generiert werden, handeln.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-sm text-white"></i>
              </span>
              4. Allgemeine Hinweise und Pflichtinformationen
            </h2>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Datenschutz
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Die Betreiber dieser Seiten nehmen den Schutz Ihrer persönlichen Daten sehr ernst. Wir behandeln Ihre
              personenbezogenen Daten vertraulich und entsprechend den gesetzlichen Datenschutzvorschriften sowie
              dieser Datenschutzerklärung.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-cookie-line text-sm text-white"></i>
              </span>
              5. Datenerfassung auf dieser Website
            </h2>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Cookies
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Unsere Internetseiten verwenden so genannte „Cookies". Cookies sind kleine Textdateien und richten auf
              Ihrem Endgerät keinen Schaden an. Sie werden entweder vorübergehend für die Dauer einer Sitzung
              (Session-Cookies) oder dauerhaft (permanente Cookies) auf Ihrem Endgerät gespeichert.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Server-Log-Dateien
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Der Provider der Seiten erhebt und speichert automatisch Informationen in so genannten Server-Log-Dateien,
              die Ihr Browser automatisch an uns übermittelt. Dies sind:
            </p>
            <ul className="list-disc list-inside text-gray-600 text-base sm:text-lg space-y-2 ml-4 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              <li>Browsertyp und Browserversion</li>
              <li>verwendetes Betriebssystem</li>
              <li>Referrer URL</li>
              <li>Hostname des zugreifenden Rechners</li>
              <li>Uhrzeit der Serveranfrage</li>
              <li>IP-Adresse</li>
            </ul>

            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Google Tag Manager
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Diese Website verwendet den Google Tag Manager (GTM). Anbieter ist die Google Ireland Limited („Google"), Gordon House, Barrow Street, Dublin 4, Irland.
            </p>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Der Google Tag Manager ist ein Tool, das es uns ermöglicht, Tracking- oder Statistik-Tools und andere Technologien auf unserer Website einzubinden. Der Google Tag Manager selbst erstellt keine Nutzerprofile, speichert keine Cookies und nimmt keine eigenständigen Analysen vor. Er dient ausschließlich der Verwaltung und Ausspielung der über ihn eingebundenen Tools. Der Google Tag Manager erfasst jedoch Ihre IP-Adresse, die auch an den Mutterkonzern von Google in die Vereinigten Staaten übertragen werden kann.
            </p>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Der Google Tag Manager wird auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO eingesetzt. Das berechtigte Interesse liegt in der effizienten Einbindung und Verwaltung von Tools auf unserer Website. Soweit eine entsprechende Einwilligung abgefragt wurde, erfolgt die Verarbeitung ausschließlich auf Grundlage von Art. 6 Abs. 1 lit. a DSGVO.
            </p>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Weitere Informationen zum Google Tag Manager finden Sie unter:{' '}
              <a
                href="https://marketingplatform.google.com/about/analytics/tag-manager/use-policy/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-teal-600 hover:text-teal-700 hover:underline transition-colors duration-300 break-all"
              >
                https://marketingplatform.google.com/about/analytics/tag-manager/use-policy/
              </a>
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Google Analytics (GA4)
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Diese Website nutzt Google Analytics 4 (GA4). Anbieter ist die Google Ireland Limited („Google"), Gordon House, Barrow Street, Dublin 4, Irland. Mess-ID: <strong className="text-gray-900">G-ZLL1TVYSSJ</strong>.
            </p>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Google Analytics 4 ermöglicht es, das Verhalten der Websitebesucher zu analysieren. Dabei werden u. a. folgende Daten erfasst: Seitenaufrufe, Verweildauer, Absprungrate, verwendete Geräte und Betriebssysteme, Herkunft des Nutzers sowie Interaktionen auf der Website. Google Analytics 4 verwendet kein Cookie-Tracking mehr als Primärmethode und setzt auf ereignisbasiertes Tracking.
            </p>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              <strong className="text-gray-900">Consent Mode v2:</strong> Wir setzen den Google Consent Mode v2 ein. Dabei werden Tracking-Daten erst dann vollständig erfasst, wenn Sie über unseren Cookie-Banner Ihre Einwilligung erteilt haben. Ohne Einwilligung werden nur anonymisierte, nicht personenbezogene Signale übermittelt. Die Einwilligung kann jederzeit durch erneuten Aufruf des Cookie-Banners widerrufen werden.
            </p>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Die Nutzung von Google Analytics erfolgt auf Grundlage Ihrer Einwilligung gemäß Art. 6 Abs. 1 lit. a DSGVO. Die Einwilligung ist freiwillig und kann jederzeit widerrufen werden.
            </p>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              <strong className="text-gray-900">Datenübermittlung in die USA:</strong> Google verarbeitet Ihre Daten u. a. in den USA. Google ist nach dem EU-US Data Privacy Framework zertifiziert, sodass ein angemessenes Datenschutzniveau gewährleistet ist. Weitere Details:{' '}
              <a
                href="https://www.privacyshield.gov/participant?id=a2zt000000001L5AAI"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-teal-600 hover:text-teal-700 hover:underline transition-colors duration-300 break-all"
              >
                EU-US Data Privacy Framework
              </a>
            </p>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              <strong className="text-gray-900">Browser-Plugin zum Deaktivieren:</strong> Sie können die Erfassung durch Google Analytics verhindern, indem Sie das Browser-Plugin herunterladen und installieren:{' '}
              <a
                href="https://tools.google.com/dlpage/gaoptout"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-teal-600 hover:text-teal-700 hover:underline transition-colors duration-300 break-all"
              >
                https://tools.google.com/dlpage/gaoptout
              </a>
            </p>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              <strong className="text-gray-900">Speicherdauer:</strong> Bei Google gespeicherte Daten auf Nutzer- und Ereignisebene werden nach 14 Monaten anonymisiert bzw. gelöscht.
            </p>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Weitere Informationen finden Sie in der Datenschutzerklärung von Google:{' '}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-teal-600 hover:text-teal-700 hover:underline transition-colors duration-300 break-all"
              >
                https://policies.google.com/privacy
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-line text-sm text-white"></i>
              </span>
              6. Kontaktformular
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Wenn Sie uns per Kontaktformular Anfragen zukommen lassen, werden Ihre Angaben aus dem Anfrageformular
              inklusive der von Ihnen dort angegebenen Kontaktdaten zwecks Bearbeitung der Anfrage und für den Fall von
              Anschlussfragen bei uns gespeichert.
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-calendar-check-line text-sm text-white"></i>
              </span>
              7. Calendly
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Wir nutzen Calendly für die Terminbuchung. Anbieter ist Calendly LLC, 271 17th St NW, 10th Floor,
              Atlanta, Georgia 30363, USA. Wenn Sie einen Termin über Calendly buchen, werden Ihre eingegebenen Daten
              (Name, E-Mail-Adresse, etc.) auf den Servern von Calendly gespeichert.
            </p>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Weitere Informationen finden Sie in der Datenschutzerklärung von Calendly:{' '}
              <a
                href="https://calendly.com/privacy"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-teal-600 hover:text-teal-700 hover:underline transition-colors duration-300 break-all"
              >
                https://calendly.com/privacy
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-global-line text-sm text-white"></i>
              </span>
              8. Readdy.ai - Website-Plattform
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Diese Website wurde mit Readdy.ai erstellt und wird über die Readdy-Plattform gehostet. Anbieter ist
              Readdy Technology Limited, 71-75 Shelton Street, Covent Garden, London, WC2H 9JQ, Vereinigtes Königreich.
            </p>
            
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Art und Umfang der Datenverarbeitung
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Beim Besuch dieser Website werden durch Readdy.ai folgende Daten automatisch erfasst und verarbeitet:
            </p>
            <ul className="list-disc list-inside text-gray-600 text-base sm:text-lg space-y-2 ml-4 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              <li>IP-Adresse des zugreifenden Rechners</li>
              <li>Datum und Uhrzeit des Zugriffs</li>
              <li>Name und URL der abgerufenen Datei</li>
              <li>Website, von der aus der Zugriff erfolgt (Referrer-URL)</li>
              <li>Verwendeter Browser und ggf. das Betriebssystem Ihres Rechners</li>
              <li>Name Ihres Access-Providers</li>
            </ul>

            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Zweck der Datenverarbeitung
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Die Verarbeitung dieser Daten erfolgt zu folgenden Zwecken:
            </p>
            <ul className="list-disc list-inside text-gray-600 text-base sm:text-lg space-y-2 ml-4 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              <li>Gewährleistung eines reibungslosen Verbindungsaufbaus der Website</li>
              <li>Gewährleistung einer komfortablen Nutzung unserer Website</li>
              <li>Auswertung der Systemsicherheit und -stabilität</li>
              <li>Optimierung unseres Internetangebots</li>
              <li>Zu administrativen Zwecken</li>
            </ul>

            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Rechtsgrundlage
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Die Rechtsgrundlage für die Datenverarbeitung ist Art. 6 Abs. 1 lit. f DSGVO. Unser berechtigtes
              Interesse folgt aus den oben aufgelisteten Zwecken zur Datenerhebung.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Speicherdauer
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Die Daten werden gelöscht, sobald sie für die Erreichung des Zweckes ihrer Erhebung nicht mehr
              erforderlich sind. Im Falle der Erfassung der Daten zur Bereitstellung der Website ist dies der Fall,
              wenn die jeweilige Sitzung beendet ist.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Content Delivery Network (CDN)
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Readdy.ai nutzt ein Content Delivery Network (CDN), um die Ladegeschwindigkeit und Performance der
              Website zu optimieren. Dabei werden Ihre Daten auf Servern in verschiedenen geografischen Regionen
              gespeichert, um Ihnen die schnellstmögliche Auslieferung der Inhalte zu gewährleisten.
            </p>

            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 mt-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Weitere Informationen
            </h3>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Weitere Informationen zum Datenschutz bei Readdy.ai finden Sie unter:{' '}
              <a
                href="https://readdy.ai/privacy"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-teal-600 hover:text-teal-700 hover:underline transition-colors duration-300 break-all"
              >
                https://readdy.ai/privacy
              </a>
            </p>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-user-settings-line text-sm text-white"></i>
              </span>
              9. Ihre Rechte
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              Sie haben das Recht:
            </p>
            <ul className="list-disc list-inside text-gray-600 text-base sm:text-lg space-y-2 ml-4 mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
              <li>Auskunft über Ihre bei uns gespeicherten personenbezogenen Daten zu verlangen</li>
              <li>Die Berichtigung unrichtiger Daten zu verlangen</li>
              <li>Die Löschung Ihrer Daten zu verlangen</li>
              <li>Die Einschränkung der Verarbeitung zu verlangen</li>
              <li>Der Verarbeitung zu widersprechen</li>
              <li>Datenübertragbarkeit zu verlangen</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0">
                <i className="ri-mail-send-line text-sm text-white"></i>
              </span>
              10. Kontakt
            </h2>
            <p className="text-gray-600 text-base sm:text-lg leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
              Bei Fragen zum Datenschutz können Sie sich jederzeit an uns wenden:
              <br />
              <br />
              <strong className="text-gray-900">E-Mail:</strong>{' '}
              <a href="mailto:info@seitenschmied.com" className="text-teal-600 hover:text-teal-700 hover:underline transition-colors duration-300">
                info@seitenschmied.com
              </a>
            </p>
          </section>
        </div>

        {/* CTA */}
        <div className="mt-8 sm:mt-12 text-center">
          <Link to="/termin-buchen">
            <button
              className="px-8 sm:px-12 py-4 sm:py-5 font-semibold text-base sm:text-lg rounded-xl transition-all duration-300 hover:shadow-2xl hover:scale-105 whitespace-nowrap cursor-pointer w-full sm:w-auto"
              style={{
                background: 'linear-gradient(135deg, #14b8a6 0%, #10b981 100%)',
                color: '#FFFFFF',
                boxShadow: '0 8px 30px rgba(20, 184, 166, 0.3)',
              }}
            >
              Kostenloses Erstgespräch buchen
            </button>
          </Link>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300 hover:scale-110 z-40 cursor-pointer"
          style={{
            background: 'linear-gradient(135deg, #14b8a6 0%, #10b981 100%)',
            color: '#FFFFFF',
          }}
        >
          <i className="ri-arrow-up-line text-xl sm:text-2xl"></i>
        </button>
      )}

      <SiteFooter />
    </div>
  );
}
